import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Eye,
  EyeOff,
  RefreshCw,
  Check,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Shield,
  MessageCircle,
  Send,
  Lock
} from "lucide-react";
import { SBER, API, tapAnimation } from "../utils/constants";

// Country codes list
const COUNTRY_CODES = [
  { code: '+996', country: 'KG', flag: '🇰🇬', name: 'Кыргызстан', format: 'XXX XXX XXX', length: 9 },
  { code: '+7', country: 'RU', flag: '🇷🇺', name: 'Россия', format: '(XXX) XXX-XX-XX', length: 10 },
  { code: '+7', country: 'KZ', flag: '🇰🇿', name: 'Казахстан', format: '(XXX) XXX-XX-XX', length: 10 },
  { code: '+373', country: 'MD', flag: '🇲🇩', name: 'Молдова', format: 'XX XXX XXX', length: 8 },
  { code: '+998', country: 'UZ', flag: '🇺🇿', name: 'Узбекистан', format: 'XX XXX XX XX', length: 9 },
  { code: '+992', country: 'TJ', flag: '🇹🇯', name: 'Таджикистан', format: 'XX XXX XXXX', length: 9 },
  { code: '+374', country: 'AM', flag: '🇦🇲', name: 'Армения', format: 'XX XXX XXX', length: 8 },
  { code: '+994', country: 'AZ', flag: '🇦🇿', name: 'Азербайджан', format: 'XX XXX XX XX', length: 9 },
  { code: '+90', country: 'TR', flag: '🇹🇷', name: 'Турция', format: 'XXX XXX XX XX', length: 10 },
  { code: '+380', country: 'UA', flag: '🇺🇦', name: 'Украина', format: 'XX XXX XX XX', length: 9 },
  { code: '+375', country: 'BY', flag: '🇧🇾', name: 'Беларусь', format: 'XX XXX-XX-XX', length: 9 },
];

const WebAuthScreen = ({ mode, setMode, onLogin, onRegister, t, language, setLanguage, currentLanguage, languages }) => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form'); // form, verification, complete
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [showCodePopup, setShowCodePopup] = useState(false);
  const [receivedCode, setReceivedCode] = useState('');
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]); // Default: Kyrgyzstan
  const [showCountryModal, setShowCountryModal] = useState(false);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    const maxLength = selectedCountry.length;
    const trimmedDigits = digits.slice(0, maxLength);
    
    // Simple formatting based on country
    if (selectedCountry.code === '+7') {
      // Russia/Kazakhstan format: (XXX) XXX-XX-XX
      if (trimmedDigits.length <= 3) return trimmedDigits;
      if (trimmedDigits.length <= 6) return `(${trimmedDigits.slice(0, 3)}) ${trimmedDigits.slice(3)}`;
      if (trimmedDigits.length <= 8) return `(${trimmedDigits.slice(0, 3)}) ${trimmedDigits.slice(3, 6)}-${trimmedDigits.slice(6)}`;
      return `(${trimmedDigits.slice(0, 3)}) ${trimmedDigits.slice(3, 6)}-${trimmedDigits.slice(6, 8)}-${trimmedDigits.slice(8)}`;
    } else if (selectedCountry.code === '+996') {
      // Kyrgyzstan format: XXX XXX XXX
      if (trimmedDigits.length <= 3) return trimmedDigits;
      if (trimmedDigits.length <= 6) return `${trimmedDigits.slice(0, 3)} ${trimmedDigits.slice(3)}`;
      return `${trimmedDigits.slice(0, 3)} ${trimmedDigits.slice(3, 6)} ${trimmedDigits.slice(6)}`;
    } else {
      // Generic format with spaces every 3 digits
      return trimmedDigits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only keep digits
    const digits = value.replace(/\D/g, '');
    const formatted = formatPhone(digits);
    setPhone(formatted);
  };

  const getCleanPhone = () => {
    // Return full phone with country code
    const digits = phone.replace(/\D/g, '');
    return selectedCountry.code.replace('+', '') + digits;
  };

  const getPhonePlaceholder = () => {
    return selectedCountry.format || 'XXX XXX XXX';
  };

  // Step 1: Send verification code
  const handleSendVerification = async () => {
    setError('');
    setLoading(true);

    const phoneDigits = phone.replace(/\D/g, '');
    const expectedLength = selectedCountry.length;
    if (phoneDigits.length !== expectedLength) {
      setError(t('auth.enterPhone'));
      setLoading(false);
      return;
    }

    if (!firstName.trim()) {
      setError(t('auth.enterName'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('auth.minPassword'));
      setLoading(false);
      return;
    }

    // Get Telegram ID from Mini App (multiple methods for compatibility)
    const tg = window.Telegram?.WebApp;
    let telegramId = null;
    
    // Method 1: initDataUnsafe (most common)
    if (tg?.initDataUnsafe?.user?.id) {
      telegramId = tg.initDataUnsafe.user.id;
      console.log('Got telegram_id from initDataUnsafe:', telegramId);
    }
    
    // Method 2: Parse initData manually
    if (!telegramId && tg?.initData) {
      try {
        const params = new URLSearchParams(tg.initData);
        const userParam = params.get('user');
        if (userParam) {
          const userData = JSON.parse(decodeURIComponent(userParam));
          telegramId = userData.id;
          console.log('Got telegram_id from parsed initData:', telegramId);
        }
      } catch (e) {
        console.error('Failed to parse initData:', e);
      }
    }
    
    // Method 3: URL hash parameters (for some Telegram versions)
    if (!telegramId) {
      try {
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const tgWebAppData = hashParams.get('tgWebAppData');
        if (tgWebAppData) {
          const dataParams = new URLSearchParams(tgWebAppData);
          const userParam = dataParams.get('user');
          if (userParam) {
            const userData = JSON.parse(decodeURIComponent(userParam));
            telegramId = userData.id;
            console.log('Got telegram_id from URL hash:', telegramId);
          }
        }
      } catch (e) {
        console.error('Failed to parse URL hash:', e);
      }
    }
    
    console.log('Final telegram_id:', telegramId, 'tg object:', tg);

    try {
      const res = await axios.post(`${API}/auth/send-verification`, { 
        phone: getCleanPhone(),
        telegram_id: telegramId  // Передаём telegram_id для отправки кода
      });
      
      if (res.data.success) {
        setVerificationMethod(res.data.method);
        setStep('verification');
        setResendCountdown(60);
        
        // Показываем код в popup если он пришёл
        if (res.data.code) {
          setReceivedCode(res.data.code);
          setShowCodePopup(true);
          // Автоматически заполняем код
          setVerificationCode(res.data.code);
        }
        
        if (res.data.method === 'telegram') {
          toast.success(t('actions.copied'));
        } else if (res.data.method === 'bot_verify') {
          toast.info(t('actions.copied'));
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.somethingWrong'));
    }
    setLoading(false);
  };

  // Step 2: Verify code
  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);

    if (verificationCode.length !== 6) {
      setError('Введите 6-значный код');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(`${API}/auth/verify-code`, {
        phone: getCleanPhone(),
        code: verificationCode
      });

      if (res.data.success && res.data.verified) {
        // Complete registration
        const regRes = await axios.post(`${API}/auth/complete-registration`, {
          phone: getCleanPhone(),
          code: verificationCode,
          password: password,
          first_name: firstName
        });

        if (regRes.data.success) {
          toast.success('Регистрация завершена!');
          // Call the original onRegister callback with the result
          onRegister(getCleanPhone(), password, firstName, regRes.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Неверный код');
    }
    setLoading(false);
  };

  // Resend code
  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    
    const tg = window.Telegram?.WebApp;
    const telegramId = tg?.initDataUnsafe?.user?.id;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-verification`, { 
        phone: getCleanPhone(),
        telegram_id: telegramId
      });
      if (res.data.success) {
        setResendCountdown(60);
        // Показываем новый код если пришёл
        if (res.data.code) {
          setReceivedCode(res.data.code);
          setVerificationCode(res.data.code);
          setShowCodePopup(true);
        }
        toast.success('Код отправлен повторно');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка отправки кода');
    }
    setLoading(false);
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const phoneDigits = phone.replace(/\D/g, '');
    const expectedLength = selectedCountry.length;
    if (phoneDigits.length !== expectedLength) {
      setError(t('auth.enterPhone'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('auth.minPassword'));
      setLoading(false);
      return;
    }

    const fullPhone = getCleanPhone();
    const result = await onLogin(fullPhone, password);
    if (!result.success) {
      setError(result.message || t('auth.invalidCredentials'));
    }
    setLoading(false);
  };

  // Forgot password - send verification
  const handleForgotSendCode = async () => {
    setError('');
    setLoading(true);

    const phoneDigits = phone.replace(/\D/g, '');
    const expectedLength = selectedCountry.length;
    if (phoneDigits.length !== expectedLength) {
      setError(t('auth.enterPhone'));
      setLoading(false);
      return;
    }

    try {
      const fullPhone = getCleanPhone();
      const res = await axios.post(`${API}/auth/forgot-password`, { 
        phone: fullPhone
      });
      
      if (res.data.success) {
        setVerificationMethod(res.data.method || 'telegram');
        setStep('forgot-verify');
        setResendCountdown(60);
        if (res.data.code) {
          setReceivedCode(res.data.code);
          setShowCodePopup(true);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Пользователь не найден');
    }
    setLoading(false);
  };

  // Forgot password - verify code and set new password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleResetPassword = async () => {
    setError('');
    
    if (verificationCode.length !== 6) {
      setError('Введите 6-значный код');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Пароль должен быть не менее 6 символов');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    setLoading(true);
    try {
      const fullPhone = getCleanPhone();
      const res = await axios.post(`${API}/auth/reset-password`, {
        phone: fullPhone,
        code: verificationCode,
        new_password: newPassword
      });
      
      if (res.data.success) {
        toast.success('Пароль успешно изменен!');
        setMode('login');
        setStep('form');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setVerificationCode('');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Неверный код или ошибка');
    }
    setLoading(false);
  };

  // Registration flow with verification
  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    handleSendVerification();
  };

  // Verification step UI
  if (step === 'verification') {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ background: SBER.bg }}>
        {/* Code Popup */}
        {showCodePopup && receivedCode && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-5"
            style={{ background: 'rgba(0,0,0,0.5)' }}
          >
            <motion.div
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              className="w-full max-w-sm rounded-2xl p-6 text-center"
              style={{ background: SBER.white }}
            >
              <div 
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ background: SBER.greenPale }}
              >
                <CheckCircle className="w-8 h-8" style={{ color: SBER.green }} />
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: SBER.dark }}>
                Ваш код подтверждения
              </h2>
              <p className="text-sm mb-4" style={{ color: SBER.gray }}>
                Код автоматически введён в поле
              </p>
              <div 
                className="py-4 px-6 rounded-xl mb-4"
                style={{ background: SBER.greenPale }}
              >
                <span 
                  className="text-3xl font-bold tracking-widest"
                  style={{ color: SBER.green, letterSpacing: '0.3em' }}
                >
                  {receivedCode}
                </span>
              </div>
              <motion.button
                whileTap={tapAnimation}
                onClick={() => setShowCodePopup(false)}
                className="w-full py-3 rounded-xl font-semibold text-white"
                style={{ background: SBER.green }}
              >
                Понятно
              </motion.button>
            </motion.div>
          </motion.div>
        )}
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <div 
              className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'linear-gradient(135deg, #1a472a 0%, #22c55e 100%)' }}
            >
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold" style={{ color: SBER.dark }}>Подтверждение</h1>
            <p className="text-sm mt-1" style={{ color: SBER.gray }}>
              Введите код подтверждения
            </p>
          </div>

          <div className="rounded-2xl p-6" style={{ background: SBER.white, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            {/* Code display if received */}
            {receivedCode && (
              <div 
                className="p-4 rounded-xl mb-4 cursor-pointer"
                style={{ background: SBER.greenPale }}
                onClick={() => setShowCodePopup(true)}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: SBER.green }}
                  >
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: SBER.dark }}>
                      Ваш код: <span className="font-bold tracking-wider" style={{ color: SBER.green }}>{receivedCode}</span>
                    </p>
                    <p className="text-xs" style={{ color: SBER.gray }}>Код уже введён ниже</p>
                  </div>
                </div>
              </div>
            )}

            {/* Instructions if code not auto-received */}
            {!receivedCode && (
              <div className="p-4 rounded-xl mb-4" style={{ background: '#DBEAFE' }}>
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    {verificationMethod === 'telegram' ? (
                      <>
                        <p>Код подтверждения отправлен в чат бота @polytech_wallet_bot</p>
                        <p className="mt-1 text-xs opacity-75">Сверните это окно и проверьте сообщения в боте</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium">Для получения кода:</p>
                        <p className="mt-1">1. Откройте бот <a href="https://t.me/polytech_wallet_bot" target="_blank" rel="noopener noreferrer" className="underline">@polytech_wallet_bot</a></p>
                        <p>2. Отправьте команду /verify</p>
                        <p>3. Введите ваш номер телефона</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  Код подтверждения
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-4 rounded-xl border-0 focus:ring-2 focus:ring-green-500 outline-none text-center text-2xl font-bold tracking-widest"
                  style={{ background: SBER.grayPale, letterSpacing: '0.5em' }}
                  placeholder="••••••"
                  maxLength={6}
                  data-testid="verification-code"
                />
              </div>

              {error && (
                <div className="p-3 rounded-xl text-sm text-red-700" style={{ background: '#FEE2E2' }}>
                  {error}
                </div>
              )}

              <motion.button
                whileTap={tapAnimation}
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
                className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: SBER.green }}
                data-testid="verify-submit"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Подтвердить
                  </>
                )}
              </motion.button>

              <div className="text-center">
                <button
                  onClick={handleResendCode}
                  disabled={resendCountdown > 0 || loading}
                  className="text-sm disabled:opacity-50"
                  style={{ color: resendCountdown > 0 ? SBER.gray : SBER.green }}
                >
                  {resendCountdown > 0 ? (
                    `Отправить повторно через ${resendCountdown}с`
                  ) : (
                    'Отправить код повторно'
                  )}
                </button>
              </div>

              <div className="text-center pt-2">
                <button
                  onClick={() => { setStep('form'); setError(''); setVerificationCode(''); }}
                  className="text-sm"
                  style={{ color: SBER.gray }}
                >
                  ← Изменить номер телефона
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Forgot password - verify and reset UI
  if (step === 'forgot-verify') {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ background: SBER.bg }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="rounded-3xl p-6" style={{ background: SBER.white, boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
            <div className="text-center mb-6">
              <div 
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ background: SBER.greenPale }}
              >
                <Lock className="w-8 h-8" style={{ color: SBER.green }} />
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: SBER.dark }}>
                Восстановление пароля
              </h2>
              <p className="text-sm" style={{ color: SBER.gray }}>
                Введите код из Telegram и новый пароль
              </p>
            </div>

            <div className="space-y-4">
              {/* Verification Code */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  Код подтверждения
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Введите 6-значный код"
                  className="w-full px-4 py-3 rounded-xl border-2 text-center text-lg tracking-widest"
                  style={{ borderColor: SBER.grayPale, color: SBER.dark }}
                  data-testid="forgot-code-input"
                />
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  Новый пароль
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Минимум 6 символов"
                    className="w-full px-4 py-3 rounded-xl border-2 pr-12"
                    style={{ borderColor: SBER.grayPale, color: SBER.dark }}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? 
                      <EyeOff className="w-5 h-5" style={{ color: SBER.gray }} /> : 
                      <Eye className="w-5 h-5" style={{ color: SBER.gray }} />
                    }
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  Подтвердите пароль
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Повторите новый пароль"
                  className="w-full px-4 py-3 rounded-xl border-2"
                  style={{ borderColor: SBER.grayPale, color: SBER.dark }}
                  data-testid="confirm-password-input"
                />
              </div>

              {error && (
                <div className="p-3 rounded-xl text-sm text-red-700" style={{ background: '#FEE2E2' }}>
                  {error}
                </div>
              )}

              <motion.button
                whileTap={tapAnimation}
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: SBER.green }}
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  'Сменить пароль'
                )}
              </motion.button>

              <div className="text-center">
                <button
                  onClick={() => { 
                    setStep('form'); 
                    setMode('login');
                    setError(''); 
                    setVerificationCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-sm"
                  style={{ color: SBER.gray }}
                >
                  ← Вернуться к входу
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Main form UI
  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: SBER.bg }}>
      {/* Language Modal */}
      <AnimatePresence>
        {showLanguageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowLanguageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-xs rounded-2xl overflow-hidden mx-4"
              style={{ background: SBER.white }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b" style={{ borderColor: SBER.grayPale }}>
                <h2 className="text-[16px] font-semibold text-center" style={{ color: SBER.dark }}>{t('profile.language.select')}</h2>
              </div>
              <div className="p-3 space-y-2">
                {languages.map((lang) => (
                  <motion.button
                    key={lang.code}
                    whileTap={tapAnimation}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowLanguageModal(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl"
                    style={{ background: language === lang.code ? SBER.greenPale : SBER.grayPale }}
                    data-testid={`auth-language-${lang.code}`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="flex-1 text-left font-medium" style={{ color: SBER.dark }}>{lang.name}</span>
                    {language === lang.code && <Check className="w-5 h-5" style={{ color: SBER.green }} />}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Country Code Modal */}
      <AnimatePresence>
        {showCountryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowCountryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-xs rounded-2xl overflow-hidden mx-4 max-h-[70vh] flex flex-col"
              style={{ background: SBER.white }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b" style={{ borderColor: SBER.grayPale }}>
                <h2 className="text-[16px] font-semibold text-center" style={{ color: SBER.dark }}>
                  {language === 'en' ? 'Select country' : language === 'kg' ? 'Өлкөнү тандаңыз' : 'Выберите страну'}
                </h2>
              </div>
              <div className="p-3 space-y-2 overflow-y-auto">
                {COUNTRY_CODES.map((country, idx) => (
                  <motion.button
                    key={`${country.code}-${country.country}-${idx}`}
                    whileTap={tapAnimation}
                    onClick={() => {
                      setSelectedCountry(country);
                      setPhone(''); // Reset phone when country changes
                      setShowCountryModal(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl"
                    style={{ 
                      background: selectedCountry.country === country.country && selectedCountry.code === country.code 
                        ? SBER.greenPale 
                        : SBER.grayPale 
                    }}
                    data-testid={`country-${country.country}`}
                  >
                    <span className="text-xl">{country.flag}</span>
                    <span className="flex-1 text-left font-medium" style={{ color: SBER.dark }}>{country.name}</span>
                    <span className="text-sm font-medium" style={{ color: SBER.gray }}>{country.code}</span>
                    {selectedCountry.country === country.country && selectedCountry.code === country.code && (
                      <Check className="w-5 h-5" style={{ color: SBER.green }} />
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div 
            className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'linear-gradient(135deg, #1a472a 0%, #22c55e 100%)' }}
          >
            <img src="/polytech-logo.png" alt="PolyTech" className="w-12 h-12 object-contain" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: SBER.dark }}>PolyTech Wallet</h1>
          <p className="text-sm mt-1" style={{ color: SBER.gray }}>
            {mode === 'login' ? t('auth.login') : mode === 'forgot' ? 'Восстановление пароля' : t('auth.register')}
          </p>
          {/* Language selector button */}
          <motion.button
            whileTap={tapAnimation}
            onClick={() => setShowLanguageModal(true)}
            className="mt-3 mx-auto flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: SBER.grayPale }}
            data-testid="auth-language-btn"
          >
            <span className="text-lg">{currentLanguage?.flag}</span>
            <span className="text-[13px] font-medium" style={{ color: SBER.gray }}>{currentLanguage?.name}</span>
            <ChevronRight className="w-4 h-4" style={{ color: SBER.grayLight }} />
          </motion.button>
        </div>

        {/* Form */}
        <div className="rounded-2xl p-6" style={{ background: SBER.white, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <form onSubmit={mode === 'login' ? handleLogin : mode === 'forgot' ? (e) => { e.preventDefault(); handleForgotSendCode(); } : handleRegisterSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  {t('auth.name')}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-0 focus:ring-2 focus:ring-green-500 outline-none"
                  style={{ background: SBER.grayPale }}
                  placeholder={language === 'en' ? 'John' : language === 'kg' ? 'Айбек' : 'Иван'}
                  data-testid="register-name"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                {t('auth.phoneNumber')}
              </label>
              <div className="flex items-center rounded-xl overflow-hidden" style={{ background: SBER.grayPale }}>
                {/* Country Code Selector */}
                <motion.button
                  type="button"
                  whileTap={tapAnimation}
                  onClick={() => setShowCountryModal(true)}
                  className="flex items-center gap-1.5 px-3 py-3 shrink-0 border-r"
                  style={{ borderColor: '#E5E7EB' }}
                  data-testid="country-selector"
                >
                  <span className="text-lg">{selectedCountry.flag}</span>
                  <span className="text-sm font-medium" style={{ color: SBER.dark }}>{selectedCountry.code}</span>
                  <ChevronDown className="w-4 h-4" style={{ color: SBER.gray }} />
                </motion.button>
                {/* Phone Input */}
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  className="flex-1 px-3 py-3 border-0 focus:ring-0 outline-none bg-transparent"
                  placeholder={getPhonePlaceholder()}
                  data-testid="auth-phone"
                />
              </div>
            </div>

            {mode !== 'forgot' && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-0 focus:ring-2 focus:ring-green-500 outline-none pr-12"
                  style={{ background: SBER.grayPale }}
                  placeholder={mode === 'login' ? t('auth.password') : t('auth.createPassword')}
                  data-testid="auth-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? 
                    <EyeOff className="w-5 h-5" style={{ color: SBER.gray }} /> : 
                    <Eye className="w-5 h-5" style={{ color: SBER.gray }} />
                  }
                </button>
              </div>
            </div>
            )}

            {error && (
              <div className="p-3 rounded-xl text-sm text-red-700" style={{ background: '#FEE2E2' }}>
                {error}
              </div>
            )}

            <motion.button
              whileTap={tapAnimation}
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: SBER.green }}
              data-testid="auth-submit"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                mode === 'login' ? t('auth.loginButton') : mode === 'forgot' ? 'Отправить код' : (
                  <>
                    <Send className="w-5 h-5" />
                    {t('actions.continue')}
                  </>
                )
              )}
            </motion.button>
          </form>

          {mode === 'login' && (
            <div className="mt-3 text-center">
              <button
                onClick={() => {
                  setMode('forgot');
                  setError('');
                  setStep('form');
                }}
                className="text-sm underline"
                style={{ color: SBER.gray }}
                data-testid="forgot-password-btn"
              >
                {t('auth.forgotPassword') || 'Забыли пароль?'}
              </button>
            </div>
          )}

          {mode === 'forgot' && (
            <div className="mt-3 text-center">
              <button
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
                className="text-sm"
                style={{ color: SBER.gray }}
              >
                ← Вернуться к входу
              </button>
            </div>
          )}

          {mode !== 'forgot' && (
          <div className="mt-4 text-center">
            <button
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError('');
                setStep('form');
              }}
              className="text-sm"
              style={{ color: SBER.green }}
            >
              {mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}
            </button>
          </div>
          )}
        </div>

        {/* Telegram hint */}
        <div className="mt-6 text-center">
          <p className="text-xs" style={{ color: SBER.grayLight }}>
            {t('auth.orUseTelegram')}
          </p>
        </div>

        <p className="text-center mt-6 text-xs" style={{ color: SBER.grayLight }}>
          {t('general.copyright')}
        </p>
      </motion.div>
    </div>
  );
};

export default WebAuthScreen;
